import { useId, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { useAtomValue } from "jotai";
import { Button, ErrorState, Spinner } from "@common/index";
import { ComponentColor, ComponentSize } from "@common/types";
import { useLocationApi } from "@/hooks/apiHooks/useLocationApi";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { useMountRead } from "@/hooks/useMountRead";
import {
    locationsAtom,
    isProcessingLocationsAtom,
} from "@/store/locationAtoms";
import { compareByCreatedAtThenName } from "@/store/shipToAtoms";
import { LocationStatus } from "@models/location";
import {
    certificateFileError,
    CERTIFICATE_REQUIRED_MESSAGE,
} from "@utils/certificateFile";
import { uploadCertErrorMessage } from "../_helpers/uploadCertError";

interface UploadCertificateModalProps {
    // The location to start on (a row's action, or the one just added). If it
    // can't take a certificate the modal says so; it never picks another.
    // Without it, the API's ship-to default if it is listed, else the first one.
    initialLocationId?: string;
    // Fires once the server has accepted the file; the parent confirms and
    // closes. Named so the confirmation can say WHICH location it was for.
    onUploaded: (locationName: string) => void;
    onClose: () => void;
}

// Customer resale-certificate upload (E12-S5 / #240, #437). Certificates are
// per LOCATION (Q14), so the modal reads the customer's locations first and
// asks which one when there is more than one candidate. An APPROVED location's
// new certificate is re-reviewed; a PENDING one's is reviewed by its approval,
// which needs one (#437). The server 409s any other status (a rejected location
// resubmits from the pending wall; one waiting for payment is past review), so
// listing them would invite a guaranteed failure. The 409 still has its own
// message for the race where a status changes between the list and the submit.
export const UploadCertificateModal = ({
    initialLocationId,
    onUploaded,
    onClose,
}: UploadCertificateModalProps) => {
    const { getLocations, uploadLocationCert } = useLocationApi();
    const locations = useAtomValue(locationsAtom);
    const isProcessing = useAtomValue(isProcessingLocationsAtom);

    // The status of THIS modal's read (it mounts per open), never the shared
    // location atoms: any surface's GET /locations (the header switcher's
    // Retry, for one) raises the global fetching flag, and gating the form on it
    // unmounted the file input mid-pick; and another surface's read failing
    // after this one succeeded took the form away (#407). Loading from the
    // first paint, so never "none of your locations is approved" before the
    // list is read — locationsAtom's [] cannot tell "never read" from "none"
    // (#228). Retry goes back to loading.
    const { readStatus, reload: retryLocations } = useMountRead(getLocations);
    // Once this modal has read the list, a later re-read (getLocations changes
    // with the session) is a background one: in flight or failed, it keeps the
    // form, and the file picked in it, on screen (PR 374 review 2).
    const [hasReadList, setHasReadList] = useState(false);
    if (readStatus === "loaded" && !hasReadList) {
        setHasReadList(true);
    }
    const isLoadingList = readStatus === "loading" && !hasReadList;

    // "" until the user picks; selectedLocation below says what the default is.
    const [selectedId, setSelectedId] = useState(initialLocationId ?? "");
    const [file, setFile] = useState<File | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const fileInputId = useId();
    const locationSelectId = useId();

    // Closing mid-upload would orphan the in-flight request's outcome, so
    // every close path is shut while isProcessing: the hook refuses Escape
    // and the backdrop, and the Cancel and Close buttons are disabled.
    // The form arrives after the fetch, so the panel itself takes focus on
    // open.
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing,
        onRequestClose: onClose,
        focusPanelOnOpen: true,
    });

    // Listed in the header's ship-to switcher's order.
    const eligibleLocations = locations
        .filter(
            (location) =>
                location.isActive &&
                (location.status === LocationStatus.Approved ||
                    location.status === LocationStatus.Pending),
        )
        .sort(compareByCreatedAtThenName);
    // Opened for one location, the modal takes that one or none: quietly
    // swapping in another would put the certificate on the wrong store.
    const isInitialIneligible =
        initialLocationId !== undefined &&
        !eligibleLocations.some((location) => location.id === initialLocationId);
    // With no pick, the location the API flags as its ship-to default, which is
    // the one the header names when nothing is stored (#404); otherwise, as for
    // a Pending-only list, the first one listed.
    const selectedLocation = isInitialIneligible
        ? undefined
        : (eligibleLocations.find((location) => location.id === selectedId) ??
            eligibleLocations.find((location) => location.isDefaultShipTo) ??
            eligibleLocations[0]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFile(event.target.files?.[0] ?? null);
        setValidationError(null);
        setSubmitError(null);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!selectedLocation) {
            return;
        }

        if (!file) {
            setValidationError(CERTIFICATE_REQUIRED_MESSAGE);
            return;
        }

        const fileError = certificateFileError(file);
        if (fileError) {
            setValidationError(fileError);
            return;
        }

        setSubmitError(null);

        try {
            await uploadLocationCert(selectedLocation.id, file);
            onUploaded(selectedLocation.name);
        } catch (error) {
            setSubmitError(uploadCertErrorMessage(error));
        }
    };

    // A failed fetch is its own state, never the empty message.
    const showFetchError = readStatus === "failed" && !hasReadList;
    const showContent = !isLoadingList && !showFetchError;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                {...panelProps}
                className="w-full max-w-md rounded-lg bg-base-100 p-6 text-neutral shadow-xl focus:outline-none"
            >
                <h2 id={titleId} className="mb-2 text-lg font-bold">
                    Upload Resale Certificate
                </h2>

                {isLoadingList && (
                    <div className="flex justify-center py-8">
                        <Spinner size={ComponentSize.LG} />
                        <span className="sr-only">Loading locations</span>
                    </div>
                )}

                {showFetchError && (
                    <ErrorState
                        message="Couldn't load your locations."
                        onRetry={retryLocations}
                    />
                )}

                {showContent && selectedLocation && (
                    <form onSubmit={handleSubmit} noValidate>
                        <p className="mb-4 text-sm text-neutral/70">
                            {selectedLocation.status === LocationStatus.Pending
                                ? "We review this certificate before we approve the location."
                                : "The new certificate is added to the location's file and reviewed by our team. Your approval is not affected."}
                        </p>

                        {eligibleLocations.length > 1 ? (
                            <label
                                htmlFor={locationSelectId}
                                className="mb-4 block"
                            >
                                <span className="mb-1 block text-sm font-semibold">
                                    Location
                                </span>
                                <select
                                    id={locationSelectId}
                                    className="select select-bordered w-full bg-white text-neutral"
                                    value={selectedLocation.id}
                                    onChange={(event) =>
                                        setSelectedId(event.target.value)
                                    }
                                >
                                    {eligibleLocations.map((location) => (
                                        <option
                                            key={location.id}
                                            value={location.id}
                                        >
                                            {location.name}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        ) : (
                            <p className="mb-4 text-sm">
                                Location:{" "}
                                <span className="font-semibold">
                                    {selectedLocation.name}
                                </span>
                            </p>
                        )}

                        <label
                            htmlFor={fileInputId}
                            className="mb-1 block text-sm font-semibold"
                        >
                            Certificate file
                        </label>
                        <input
                            id={fileInputId}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="file-input file-input-bordered w-full bg-white text-neutral"
                            onChange={handleFileChange}
                        />
                        <p className="text-xs text-neutral/60 mt-1">
                            PDF, JPG, or PNG, up to 10 MB.
                        </p>
                        {validationError && (
                            <p className="text-red-500 text-sm mt-1">
                                {validationError}
                            </p>
                        )}

                        {submitError && (
                            <p role="alert" className="mt-3 text-sm text-error">
                                {submitError}
                            </p>
                        )}

                        <div className="mt-6 flex justify-end gap-2">
                            <Button
                                type="submit"
                                color={ComponentColor.Primary}
                                loading={isProcessing}
                                className="normal-case"
                            >
                                Submit
                            </Button>
                            <Button
                                color={ComponentColor.Neutral}
                                disabled={isProcessing}
                                onClick={onClose}
                                className="normal-case"
                            >
                                Cancel
                            </Button>
                        </div>
                    </form>
                )}

                {showContent && !selectedLocation && (
                    <>
                        <p className="text-sm">
                            {isInitialIneligible
                                ? "This location can't take a certificate right now."
                                : "None of your locations can take a certificate right now."}
                        </p>
                        <div className="mt-6 flex justify-end">
                            <Button
                                color={ComponentColor.Neutral}
                                disabled={isProcessing}
                                onClick={onClose}
                                className="normal-case"
                            >
                                Close
                            </Button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};
